// ── LT-1 — the router ────────────────────────────────────────────────────────
// No dependency. history.pushState + one custom event is the whole mechanism;
// react-router would be ~20kB and a version-compatibility surface for behaviour
// this app can express in eighty lines.
//
// ⛔ PATH-BASED, NEVER HASH-BASED, AND THIS IS NOT A STYLE CHOICE.
// Supabase delivers recovery and magic-link tokens as a URL FRAGMENT —
// `#access_token=…&refresh_token=…` — which the auth client reads on load. A
// hash router would own that fragment, rewrite it, and destroy the token before
// the session was established: password reset and email confirmation would fail
// in a way that looks like "the link is broken" and is nearly impossible to
// diagnose from a bug report. Nothing in this file reads or writes
// location.hash, and nothing in it ever should.
//
// PAGE IDS ARE THE PATH SEGMENTS. `/invoices` -> the invoices page;
// `/customers/<id>` -> that customer; `/new/<page>` -> the page with its "new"
// form open. That mapping is deliberately trivial so a tour, a support email or
// a customer's own bookmark can all name a screen the same way.

import { useState, useEffect } from "react";

/** The event every navigate() dispatches. popstate covers back/forward; this
 *  covers our own pushState calls, which do NOT fire popstate. */
export const NAV_EVENT = "m2:navigate";

/** Pages reachable by path. Anything else redirects to the dashboard.
 *  Includes settings, invoicetemplates and team — which are nav items, in NAV's
 *  "Settings" section in MsquaredApp.jsx, not off-nav pages — and the six
 *  "Coming soon" ids, which are real routes rendering a real screen. */
export const KNOWN_PAGES = [
  "dashboard",
  "customers", "customergroups", "intelligence", "salesorders", "deliverynotes",
  "invoices", "creditnotes", "statements", "quotations",
  "products", "pricelists", "suppliers", "inventory",
  "purchaseorders", "grn",
  "expenses", "accounts", "vat",
  "settings", "invoicetemplates", "team",
  // LT-2: the billing screen. Owner-only in the UI, but a ROUTE for everyone —
  // a non-owner who follows a link must land on a page that explains who can
  // pay, not be bounced to the dashboard as if the URL were wrong.
  "billing",
  // LT-1: routable, and each renders ComingSoon. A hand-typed /bank must land
  // somewhere honest rather than bouncing to the dashboard as if it never
  // existed — the screen is coming, the URL is not a mistake.
  "bank", "payroll", "staff", "calendar", "projects", "acctasks",
];

export const DEFAULT_PAGE = "dashboard";

/** The current path, always with a leading slash. */
export function getPath() {
  if (typeof window === "undefined") return "/";
  return window.location.pathname || "/";
}

/**
 * Change the URL and tell the app. `replace` swaps the current entry instead of
 * pushing a new one — used for corrections (unknown page, "/") so the back
 * button never walks the user back into a URL we just rejected.
 */
export function navigate(path, { replace = false } = {}) {
  if (typeof window === "undefined") return;
  let target = String(path || "/");

  // ⛔ CARRY THE FRAGMENT FORWARD. pushState("/invoices") replaces the WHOLE
  // URL, and a path with no "#" silently DROPS whatever fragment was there.
  // That is not a theoretical tidiness point: a Supabase recovery or
  // magic-link lands on "/#access_token=…", "/" is corrected to "/dashboard"
  // by replaceState on the very first render, and if that wiped the fragment
  // before the auth client had read it the user's password reset would fail
  // with nothing to see but a redirect that looks correct. supabase-js reads
  // the fragment at module load, so it normally wins the race — but "normally
  // wins the race" is not a guarantee, and this costs one line.
  //
  // An explicit fragment in `path` always wins; this only fills in a missing
  // one. Once the auth client has consumed and cleared the hash, this is a
  // no-op forever after.
  if (!target.includes("#") && window.location.hash) target += window.location.hash;

  if (replace) window.history.replaceState(null, "", target);
  else window.history.pushState(null, "", target);
  window.dispatchEvent(new CustomEvent(NAV_EVENT, { detail: { path: target } }));
}

/**
 * Parse a path into { page, param, action }.
 * Pure — no side effects, no redirect. useRoute() owns the corrections, so this
 * stays testable on its own and callable during render without surprises.
 */
export function parsePath(path) {
  const segments = String(path || "/").split("/").filter(Boolean);

  if (segments.length === 0) return { page: DEFAULT_PAGE, param: null, action: null, redirect: "/" + DEFAULT_PAGE };

  // "/new/<page>" — the quick-action route. The page id is the SECOND segment.
  if (segments[0] === "new") {
    const target = segments[1];
    if (!KNOWN_PAGES.includes(target)) {
      return { page: DEFAULT_PAGE, param: null, action: null, redirect: "/" + DEFAULT_PAGE };
    }
    return { page: target, param: null, action: "new", redirect: null };
  }

  const [page, param = null] = segments;
  if (!KNOWN_PAGES.includes(page)) {
    return { page: DEFAULT_PAGE, param: null, action: null, redirect: "/" + DEFAULT_PAGE };
  }
  return { page, param, action: null, redirect: null };
}

/**
 * The hook the app renders from. Returns { page, param, action } and keeps
 * itself in step with the back/forward buttons AND with our own navigate().
 */
export function useRoute() {
  const [path, setPath] = useState(getPath);

  useEffect(() => {
    const sync = () => setPath(getPath());
    window.addEventListener("popstate", sync);
    window.addEventListener(NAV_EVENT, sync);
    // A path can change between the first render and the effect attaching
    // (an auth redirect, say). Re-read once rather than trust the initial value.
    sync();
    return () => {
      window.removeEventListener("popstate", sync);
      window.removeEventListener(NAV_EVENT, sync);
    };
  }, []);

  const route = parsePath(path);

  // The correction is a side effect, so it belongs in an effect — redirecting
  // during render would be a setState-in-render warning in React 19 and would
  // fight StrictMode's double invocation.
  useEffect(() => {
    if (route.redirect) navigate(route.redirect, { replace: true });
  }, [route.redirect]);

  return { page: route.page, param: route.param, action: route.action };
}
